import dayjs from 'dayjs'
import { getAgeFromDobWithTime } from 'src/consumer/helpers/profilePageHelper'

export const CERTIFICATE_CATEGORY = {
  CERTIFICATE: 'CERTIFICATE',
  CONSENT: 'CONSENT',
}

export const CERTIFICATE_STATUS = {
  DRAFT: 'DRAFT',
  ISSUED: 'ISSUED',
  REVOKED: 'REVOKED',
}

export const CATEGORY_LABELS = {
  [CERTIFICATE_CATEGORY.CERTIFICATE]: 'Medical Certificate',
  [CERTIFICATE_CATEGORY.CONSENT]: 'Consent Form',
}

export const COLOR_MODE = { COLOR: 'color', BW: 'bw' }

/** Default accent colour scheme (kept in sync with the backend default). */
export const DEFAULT_ACCENT_COLOR = '#1F4E79'

export const ACCENT_COLOR_PRESETS = [
  { label: 'Deep Blue', value: '#1F4E79' },
  { label: 'Indigo', value: '#4338CA' },
  { label: 'Teal', value: '#0F766E' },
  { label: 'Maroon', value: '#7F1D1D' },
  { label: 'Slate', value: '#334155' },
  { label: 'Black', value: '#111827' },
]

const COLOR_PREFS_STORAGE_KEY = 'medicalCertificateColorPrefs'

/** Load the practitioner's saved default colour scheme (falls back to the global default). */
export function loadColorPrefs() {
  try {
    const raw = localStorage.getItem(COLOR_PREFS_STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      return {
        colorMode: parsed.colorMode === COLOR_MODE.BW ? COLOR_MODE.BW : COLOR_MODE.COLOR,
        accentColor: parsed.accentColor || DEFAULT_ACCENT_COLOR,
      }
    }
  } catch (e) {
    /* ignore */
  }
  return { colorMode: COLOR_MODE.COLOR, accentColor: DEFAULT_ACCENT_COLOR }
}

/** Persist the last-used colour scheme so it becomes the default for future certificates. */
export function saveColorPrefs(prefs) {
  try {
    localStorage.setItem(COLOR_PREFS_STORAGE_KEY, JSON.stringify(prefs))
  } catch (e) {
    /* ignore */
  }
}

/** Format a date-ish value (Date | ISO | yyyy-mm-dd | Instant string) to DD-MM-YYYY for display. */
export function formatDisplayDate(value) {
  if (!value) return ''
  const d = dayjs(value)
  return d.isValid() ? d.format('DD-MM-YYYY') : String(value)
}

/** Replace {{ token }} placeholders in an HTML string with values from the map (missing -> empty). */
export function substitutePlaceholders(html, values) {
  if (!html) return ''
  return html.replace(/\{\{\s*([\w]+)\s*\}\}/g, (_match, token) => {
    const v = values?.[token]
    return v === undefined || v === null ? '' : String(v)
  })
}

function formatDegrees(listOfDegrees) {
  if (!listOfDegrees) return ''
  if (Array.isArray(listOfDegrees)) return listOfDegrees.filter(Boolean).join(', ')
  return String(listOfDegrees)
}

function formatRegistration(registrationIds) {
  if (!registrationIds) return ''
  if (Array.isArray(registrationIds)) {
    return registrationIds
      .map((r) =>
        typeof r === 'string'
          ? r
          : r?.number || r?.registrationNumber || r?.regNumber || r?.regId || r?.value || '',
      )
      .filter(Boolean)
      .join(', ')
  }
  return String(registrationIds)
}

export function resolveDoctorName(mentorProfile) {
  if (!mentorProfile) return ''
  return (
    mentorProfile.name ||
    [mentorProfile.firstName, mentorProfile.lastName].filter(Boolean).join(' ') ||
    mentorProfile.fullName ||
    ''
  )
}

export function resolvePatientAge(patient) {
  const dob = patient?.patientDob || patient?.dateOfBirth || patient?.dob
  if (!dob) return ''
  try {
    const age = getAgeFromDobWithTime(dob)
    return age === undefined || age === null ? '' : String(age)
  } catch (e) {
    return ''
  }
}

/**
 * Auto-resolved placeholder tokens (everything the practitioner does NOT type by hand). These are
 * substituted into the template body; tokens declared in editableFields are layered on top.
 */
export function buildAutoTokenValues({ mentorProfile, patient, clinicName }) {
  return {
    doctorName: resolveDoctorName(mentorProfile),
    doctorDegrees: formatDegrees(mentorProfile?.listOfDegrees || mentorProfile?.degrees),
    doctorRegistration: formatRegistration(mentorProfile?.registrationIds),
    patientName: patient?.patientName || patient?.name || '',
    patientGender: patient?.patientGender || patient?.gender || '',
    patientAge: resolvePatientAge(patient),
    clinicName: clinicName || '',
    issueDate: formatDisplayDate(new Date()),
    today: formatDisplayDate(new Date()),
  }
}

/** Build the display-ready value map for editable fields (dates formatted for the body text). */
export function buildFieldDisplayValues(editableFields, fieldValues) {
  const out = {}
  ;(editableFields || []).forEach((field) => {
    const raw = fieldValues?.[field.token]
    out[field.token] = field.type === 'date' ? formatDisplayDate(raw) : raw || ''
  })
  return out
}

/** Tokens missing required values; used to block generation. */
export function getMissingRequiredFields(editableFields, fieldValues) {
  return (editableFields || []).filter(
    (field) => field.required && !String(fieldValues?.[field.token] || '').trim(),
  )
}
